import { RefObject, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import styles from '../HubSpotForm.module.scss';
import { reactSelectStyles } from '../reactSelectStyles';

const VALIDATION_ERROR_CLEAR_DELAY = 100;

type CheckboxGroup = {
	id: string;
	label: string;
	required: boolean;
	options: { value: string; label: string }[];
};

export function useReactSelectPortals(
	containerRef: RefObject<HTMLDivElement>,
	checkboxGroups: CheckboxGroup[],
	placeholderLabel: string,
	isMounted: boolean,
) {
	const [selectedValues, setSelectedValues] = useState<Record<string, string[]>>({});
	const [reactSelectPortals, setReactSelectPortals] = useState<JSX.Element[]>([]);

	useEffect(() => {
		if (!checkboxGroups.length || !containerRef.current || !isMounted) return;

		const form = containerRef.current.querySelector('form');
		if (!form) return;

		const domCheckboxGroups = Array.from(form.querySelectorAll('.hsfc-CheckboxFieldGroup'));
		const portals: JSX.Element[] = [];

		checkboxGroups.forEach((group, index) => {
			const domGroup = domCheckboxGroups[index] as HTMLElement | undefined;
			if (!domGroup) return;

			let portalMount = domGroup.querySelector(':scope > .hubspot-react-select') as HTMLElement | null;
			if (!portalMount) {
				portalMount = document.createElement('div');
				portalMount.className = 'hubspot-react-select';
				const checkboxOptions = domGroup.querySelector('.hsfc-CheckboxFieldGroup__Options');
				domGroup.insertBefore(portalMount, checkboxOptions ?? null);
			}

			portals.push(
				createPortal(
					<div className={styles.selectWrapper}>
						<label className={styles.selectLabel}>
							{group.label}
							{group.required && <span className={styles.selectLabelRequired}> *</span>}
						</label>
						<Select
							isMulti
							options={group.options}
							menuPortalTarget={document.body}
							styles={reactSelectStyles}
							placeholder={placeholderLabel}
							closeMenuOnSelect={false}
							value={group.options.filter((o) => selectedValues[group.id]?.includes(o.value))}
							onChange={(selected: any[]) => {
								const newSelectedValues = selected.some((o) => o.value === '__all__')
									? group.options.filter((o) => o.value !== '__all__').map((o) => o.value)
									: selected.map((o) => o.value);

								setSelectedValues((prev) => ({ ...prev, [group.id]: newSelectedValues }));

								const nativeCheckboxes = domGroup.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
								nativeCheckboxes.forEach((checkbox) => {
									const wasChecked = checkbox.checked;
									const shouldBeChecked = newSelectedValues.includes(checkbox.value);
									if (wasChecked !== shouldBeChecked) {
										checkbox.checked = shouldBeChecked;
										checkbox.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
									}
								});

								if (newSelectedValues.length > 0) {
									setTimeout(() => {
										domGroup.classList.remove('hsfc-Field--hasError');
										domGroup.querySelectorAll('.hsfc-ErrorAlert, [data-hsfc-id="ErrorAlert"]').forEach((el) => el.remove());
									}, VALIDATION_ERROR_CLEAR_DELAY);
								}
							}}
						/>
					</div>,
					portalMount,
				),
			);
		});

		setReactSelectPortals(portals);
	}, [checkboxGroups, placeholderLabel, selectedValues, isMounted]);

	return reactSelectPortals;
}
